import { Component, OnDestroy } from '@angular/core';
import { interval, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { EmailVerifyModalService } from '../../services/email-verify-modal.service';
import { Store } from '@ngrx/store';
import { selectIsArabic } from '../../store/selectors/language.selectors';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { initAuth, loginSuccess } from '../../store/actions/auth.actions';
import { ToastrService } from 'ngx-toastr';
declare var bootstrap: any;

@Component({
  selector: 'app-email-verification',
  standalone: false,
  templateUrl: './email-verification.component.html',
  styleUrl: './email-verification.component.scss',
})
export class EmailVerificationComponent implements OnDestroy {
  isVisible = false;

  email: string | undefined = '';
  code = '';
  message = '';
  resendCooldown = 0; // seconds remaining before resend allowed
  private timerSub?: Subscription;

  modalInstance: any;

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private modalService: EmailVerifyModalService,
    private store: Store,
    private traslate: TranslateService,
    private toastr: ToastrService
  ) {
    this.modalService.openModal$.pipe(takeUntil(this.destroy$)).subscribe((email) => {
      this.email = email;
      this.isVisible = true;
      this.openModal();
    });

    this.modalService.closeModal$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isVisible = false;
    });
  }

  openModal() {
    const modalEl = document.getElementById('emailModal')!;
    if (modalEl) {
      this.modalInstance = new bootstrap.Modal(modalEl);
      this.modalInstance.show();
    }
  }

  sendCode() {
    if (!this.email) {
      this.message = this.traslate.instant('EMAIL_ENTER_EMAIL');
      return;
    }

    this.authService
      .sendCode(this.email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.message = this.traslate.instant('EMAIL_CODE_SENT');
          this.startCooldown(60); // 60-second cooldown
        },
        error: (err) => {
          this.message = err.error || this.traslate.instant('EMAIL_CODE_SEND_ERROR');
        },
      });
  }

  verifyCode() {
    if (!this.email) {
      this.message = this.traslate.instant('EMAIL_ENTER_EMAIL');
      return;
    }

    if (!this.code || this.code.length < 5) {
      this.message = this.traslate.instant('EMAIL_CODE_INVALID');
      return;
    }

    this.authService
      .verifyCode(this.email, this.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          res.verified
            ? this.toastr.success(this.traslate.instant('EMAIL_VERIFIED'))
            : this.toastr.error(this.traslate.instant('EMAIL_CODE_INVALID'));
          this.store.dispatch(initAuth());
          this.modalInstance.hide();
        },
        error: () => this.toastr.error(this.traslate.instant('EMAIL_VERIFICATION_FAILED')),
      });
  }

  startCooldown(seconds: number) {
    this.resendCooldown = seconds;
    this.timerSub?.unsubscribe();

    this.timerSub = interval(1000).subscribe(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        this.timerSub?.unsubscribe();
      }
    });
  }

  ngOnDestroy() {
    this.timerSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
